import {
	AfterViewInit,
	Component,
	HostListener,
	Injectable,
	Input,
	OnDestroy,
	OnInit,
	Optional
} from '@angular/core';
import { Router } from '@angular/router';
import { cast, IQuestionnaireGroup, Messgeraete } from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem } from '@interfaces/questions';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { Subscription } from 'rxjs';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { isNil } from 'ng-zorro-antd/core/util';
import {
	AbstractSpecialQuestionComponent,
	ISpecialQuestionComponent
} from '../special-question.service';
import { AbstractReaktion } from '@workflow/components/questionnaire/questionnaire-item/special-question/abstract-reaction';
import { AkustischeReaktionEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/akustische-reaktion/akustische-reaktion-epilog.component';

export type AkustischeReaktionResult = {[key:number]: number};

@Injectable({
	providedIn: 'root',
})
export class AkustischeReaktion extends AbstractReaktion {
	readonly COUNT = 10;
	public readonly component = AkustischeReaktionComponent;
	public readonly specialQuestionType = 'AkustischeReaktion';
	public readonly requiredDevices = [Messgeraete.SoundDevice];

	title = 'Akustische Reaktion';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: 'Akustische Reaktion',
		preface: ['Zur Bestimmung der akustischen Reaktion wird im folgenden Test 10-mal ein Ton ' +
		'in unterschiedlichen Zeitabständen abgespielt. ' +
		'Sobald Sie den Ton hören, "löschen" Sie diesen so schnell wie möglich mit dem Buzzer. ' +
		'Sie beginnen den Test durch Betätigen der “Test Starten” Schaltfläche auf der nächsten Seite.'],
		items: [
			cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Akustische Reaktion - Messwerte I - V',
				items: [
					{
						label: 'Messung 1',
						key: '0',
					}, {
						label: 'Messung 2',
						key: '1',
					}, {
						label: 'Messung 3',
						key: '2',
					}, {
						label: 'Messung 4',
						key: '3',
					}, {
						label: 'Messung 5',
						key: '4',
					},
				],
				default: cast<RangeQuestion>({
					type: 'range',
					rangeLow: 0,
					rangeHigh: 1000,
					steps: 5,
					unit: 'msek',
				}),
				isProcedure: true,
			}), cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Akustische Reaktion - Messwerte VI - X',
				items: [
					{
						label: 'Messung 6',
						key: '5',
					}, {
						label: 'Messung 7',
						key: '6',
					}, {
						label: 'Messung 8',
						key: '7',
					}, {
						label: 'Messung 9',
						key: '8',
					}, {
						label: 'Messung 10',
						key: '9',
					},
				],
				default: cast<RangeQuestion>({
					type: 'range',
					rangeLow: 0,
					rangeHigh: 1000,
					steps: 5,
					unit: 'msek',
				}),
				isProcedure: true,
			}),
		],
		startText: 'actions.goToTest',
	};

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: specialQuestion,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: AkustischeReaktionEpilogComponent,
			key: specialQuestion.key,
		}];
	}

}

@Component({
	selector: 'workflow-akustische-reaktion',
	templateUrl: './akustische-reaktion.component.html',
	styleUrls: ['./akustische-reaktion.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class AkustischeReaktionComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, AfterViewInit, ISpecialQuestionComponent {

	public static count = 10;

	private results: number[] = [];
	private startTime = 0;
	private timerMin = 1500;
	private timerMax = 3500;
	private ioDelay = 90; // gemessene 90 ms Verzögerung

	private audio = new Audio('assets/sounds/Akustische Reaktion 1024 Hz -20db.mp3');
	private countdownTimer;
	private subscription = new Subscription();
	public isTestRunning: boolean;

	public buttonText = 'actions.starttest';

	@Input() page: number;

	constructor(@Optional() private workflowItemKey: WorkflowItemKeyDirective, router: Router) {
		super(router);
	}

	async dispatchVitaReact($event: Event): Promise<void> {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:keydown', ['$event'])
	keyPress($event: Event): void {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:vitareact-press', ['$event'])
	vitaReactPress($event: Event): void {
		this.hitButton($event.timeStamp);
	}

	ngOnInit(): void {
		this.audio.load();
		this.audio.pause();

		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.results = data && typeof(data[0]) === 'number' ? data as number[] : [];
				this.goToEpilog();
			}
		}));
	}

	ngOnDestroy(): void {
		this.resetTimer();
		this.resetAudio();
		this.subscription.unsubscribe();
	}

	ngAfterViewInit(): void {
		this.results = [];
		this.resetTimer();
		this.resetAudio();
	}

	private get completed(): boolean {
		return this.results?.length >= AkustischeReaktionComponent.count;
	}

	private resetAudio() {
		this.audio.pause();
		this.audio.currentTime = 0;
	}

	private checkProgressAndMove(): void {
		const completed = this.completed;
		this.valid.emit(completed);

		if (completed) {
			this.updateResults();
			this.goToEpilog();
			return;
		} else {
			this.setTimer();
		}
		this.buttonText = 'actions.stop';
	}

	private updateResults(): void {
		this.workflowItemKey.setProperty({...this.results});
	}

	private setTimer(): Promise<void> {
		if (this.isTestRunning) {
			return;
		}
		this.isTestRunning = true;

		const elementTimer = Math.floor(Math.random() * (this.timerMax - this.timerMin + 1)) + this.timerMin;

		this.audio.load();

		this.countdownTimer = setTimeout(() => {
			this.audio.play()
				.finally(() => {
					this.startTime = performance.now();
				})
				.catch(console.error);
		}, elementTimer);
	}

	private resetTimer(): void {
		this.startTime = null;
		this.isTestRunning = false;
		clearTimeout(this.countdownTimer);
	}

	hitButton(eventTimestamp: number): void {
		if (!this.finishedAndLocked && !this.completed) {
			if (!this.isTestRunning) {
				this.setTimer();
			} else if (!isNil(this.startTime)) {
				const duration = eventTimestamp - this.startTime - this.ioDelay;
				if (duration < 0) {
					return;
				}

				this.isTestRunning = false;
				this.results.push(duration);

				this.resetTimer();
				this.resetAudio();
			}
			this.checkProgressAndMove();
		}
	}
}
